import chalk from 'chalk';
import { prompt } from './prompt';

interface Patch {
  version: string;
  message: string;
  is_latest: boolean;
}

interface Release {
  version: string;
  patches: Patch[];
}

interface HistoryData {
  appId: string;
  platform: string;
  releases: Release[];
}

export class InteractiveHistory {
  private data: HistoryData;
  private currentReleaseIndex = 0;
  private currentPatchPage = 0;
  private patchesPerPage = 5;

  constructor(data: HistoryData) {
    this.data = data;
  }

  async start(appId: string, platform: string) {
    console.log(chalk.blue(`\n📱 App ID: ${appId}`));
    console.log(chalk.blue(`🔧 Platform: ${platform}\n`));

    if (!this.data.releases || this.data.releases.length === 0) {
      console.log(chalk.yellow("⚠️  No releases found for this platform.\n"));
      return;
    }

    await this.showCurrentView(appId, platform);
  }

  private async showCurrentView(appId: string, platform: string) {
    console.clear();
    console.log(chalk.blue(`📱 App ID: ${appId} | 🔧 Platform: ${platform}`));
    console.log(chalk.bold.white("📦 Release History\n"));

    const currentRelease = this.data.releases[this.currentReleaseIndex];
    const isLatestRelease = this.currentReleaseIndex === 0;
    const versionIcon = isLatestRelease ? "🚀" : "📋";

    // Show current release info
    console.log(chalk.green(`${versionIcon} Version: ${currentRelease.version} (${this.currentReleaseIndex + 1}/${this.data.releases.length})`));

    if (currentRelease.patches && currentRelease.patches.length > 0) {
      console.log(chalk.cyan("   📄 Patches:"));

      // Show paginated patches
      const startIndex = this.currentPatchPage * this.patchesPerPage;
      const endIndex = Math.min(startIndex + this.patchesPerPage, currentRelease.patches.length);
      const visiblePatches = currentRelease.patches.slice(startIndex, endIndex);

      visiblePatches.forEach((patch: Patch, index: number) => {
        const patchIcon = patch.is_latest ? "●" : "○";
        const patchColor = patch.is_latest ? chalk.bold.green : chalk.gray;
        const latestLabel = patch.is_latest ? " (LATEST)" : "";
        const messageText = patch.message ? ` - ${patch.message}` : "";

        console.log(patchColor(`   ${patchIcon} ${patch.version}${latestLabel}${chalk.dim(messageText)}`));
      });

      // Show pagination info for patches
      if (currentRelease.patches.length > this.patchesPerPage) {
        const totalPages = Math.ceil(currentRelease.patches.length / this.patchesPerPage);
        console.log(chalk.dim(`\n   Showing ${startIndex + 1}-${endIndex} of ${currentRelease.patches.length} patches (Page ${this.currentPatchPage + 1}/${totalPages})`));
      }
    } else {
      console.log(chalk.gray("   📄 No patches available"));
    }

    console.log("");
    await this.showNavigationMenu(appId, platform);
  }

  private async showNavigationMenu(appId: string, platform: string) {
    const options = [];
    const currentRelease = this.data.releases[this.currentReleaseIndex];

    // Release navigation
    if (this.currentReleaseIndex > 0) {
      options.push("p - Previous release");
    }
    if (this.currentReleaseIndex < this.data.releases.length - 1) {
      options.push("n - Next release");
    }

    // Patch pagination
    if (currentRelease.patches && currentRelease.patches.length > this.patchesPerPage) {
      const totalPages = Math.ceil(currentRelease.patches.length / this.patchesPerPage);
      if (this.currentPatchPage > 0) {
        options.push("u - Previous patches");
      }
      if (this.currentPatchPage < totalPages - 1) {
        options.push("d - Next patches");
      }
    }

    options.push("q - Quit");

    console.log(chalk.cyan("Navigation: ") + chalk.dim(options.join(" | ")));

    const input = await prompt("\nEnter command: ");
    await this.handleInput(appId, platform, input.toLowerCase().trim());
  }

  private async handleInput(appId: string, platform: string, input: string) {
    switch (input) {
      case 'p':
        if (this.currentReleaseIndex > 0) {
          this.currentReleaseIndex--;
          this.currentPatchPage = 0; // Reset patch page when changing release
          await this.showCurrentView(appId, platform);
        }
        break;
      case 'n':
        if (this.currentReleaseIndex < this.data.releases.length - 1) {
          this.currentReleaseIndex++;
          this.currentPatchPage = 0; // Reset patch page when changing release
          await this.showCurrentView(appId, platform);
        }
        break;
      case 'u':
        if (this.currentPatchPage > 0) {
          this.currentPatchPage--;
          await this.showCurrentView(appId, platform);
        }
        break;
      case 'd':
        const currentRelease = this.data.releases[this.currentReleaseIndex];
        const totalPages = Math.ceil(currentRelease.patches.length / this.patchesPerPage);
        if (this.currentPatchPage < totalPages - 1) {
          this.currentPatchPage++;
          await this.showCurrentView(appId, platform);
        }
        break;
      case 'q':
        console.log(chalk.green("\n👋 Goodbye!\n"));
        break;
      default:
        console.log(chalk.red("\n❌ Invalid command. Please try again."));
        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.showCurrentView(appId, platform);
        break;
    }
  }
}